// API client for backend communication
import { Pool, Entry, CreateEntryRequest, CreatePoolRequest, Team, PaymentInfo, PaymentSettings, UserBalance, OwedSummary } from '../types/pool';
import { 
  ImportTeamsRequest, 
  ImportPlayersRequest, 
  ImportScheduleRequest, 
  EnterResultRequest,
  Event,
  ImportResult 
} from '../types/admin';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

interface ApiError {
  message: string;
  statusCode: number;
}

export interface NotificationPrefs {
  deadlineReminder: boolean;
  poolLocked: boolean;
  scoreUpdates: boolean;
  /** Admin-composed announcement emails. Default true; user can opt out from Profile. */
  broadcasts: boolean;
  subscribedPoolTypes: string[]; // Empty array = all pool types
}

export interface PoolTypesResponse {
  poolTypes: string[];
  labels: Record<string, string>;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: 'An error occurred',
        statusCode: response.status,
      }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Pool endpoints
  async getPools(): Promise<Pool[]> {
    return this.request<Pool[]>('/pools');
  }

  async getEliminatedNcaaTeams(): Promise<{ eliminatedTeamIds: string[] }> {
    return this.request<{ eliminatedTeamIds: string[] }>('/pools/ncaa/eliminated-teams');
  }

  async getMyPoolIds(token: string): Promise<string[]> {
    return this.request<string[]>('/pools/my-pools', {}, token);
  }

  async getPool(poolId: string): Promise<Pool> {
    return this.request<Pool>(`/pools/${poolId}`);
  }

  async createPool(data: CreatePoolRequest, token: string): Promise<Pool> {
    return this.request<Pool>('/pools', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  // Team endpoints
  async getPoolTeams(poolId: string): Promise<Team[]> {
    return this.request<Team[]>(`/pools/${poolId}/teams`);
  }

  // Player endpoints
  async getPoolPlayers(poolId: string): Promise<{ players: any[] }> {
    return this.request<{ players: any[] }>(`/pools/${poolId}/players`);
  }

  // Event endpoints (public - for F1 race schedule)
  /**
   * Fetch the master World Cup pool's events (with team data) — used by the
   * Survivor entry form to render a Games view of actual matchups in Phase 2.
   * Public endpoint, no auth required.
   */
  async getWorldCupMasterEvents(): Promise<{ masterPoolId: string | null; events: any[] }> {
    return this.request('/pools/world-cup/master-events');
  }

  async getPoolEventsPublic(poolId: string): Promise<{ events: any[] }> {
    return this.request<{ events: any[] }>(`/pools/${poolId}/events`);
  }

  async getPlayerStats(poolId: string): Promise<any> {
    return this.request<any>(`/pools/${poolId}/player-stats`);
  }

  // Bracket endpoint for NCAA Basketball pools
  async getPoolBracket(poolId: string): Promise<{
    poolId: string;
    poolName: string;
    poolType: string;
    totalTeams: number;
    regions?: Record<string, any[]>;
    teams: Array<{
      id: string;
      name: string;
      shortName: string;
      seed: number;
      region?: string;
      conference?: string;
    }>;
  }> {
    return this.request(`/pools/${poolId}/bracket`);
  }

  // Entry endpoints
  async getPoolEntries(poolId: string): Promise<Entry[]> {
    return this.request<Entry[]>(`/pools/${poolId}/entries`);
  }

  async createEntry(
    poolId: string,
    data: CreateEntryRequest,
    token: string
  ): Promise<Entry> {
    return this.request<Entry>(`/pools/${poolId}/entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  /**
   * Get the current user's most recent entry in a pool
   * Uses the dedicated /entries/me endpoint which returns the full entry with picks
   * (unlike the leaderboard endpoint which hides picks when pool is OPEN)
   * Returns null if no entry exists (404 from server)
   */
  async getMyEntry(poolId: string, token: string): Promise<Entry | null> {
    try {
      return await this.request<Entry>(`/pools/${poolId}/entries/me`, {}, token);
    } catch (error) {
      // "Entry not found" or 404 means no entry exists - return null silently
      if (error instanceof Error && (
        error.message.toLowerCase().includes('not found') ||
        error.message.includes('404')
      )) {
        return null;
      }
      // Only log unexpected errors
      console.error('Error fetching my entry:', error);
      return null;
    }
  }

  /**
   * Get ALL of the current user's entries in a pool (supports multiple entries)
   * Returns empty array if no entries exist
   */
  async getMyEntries(poolId: string, token: string): Promise<Entry[]> {
    try {
      return await this.request<Entry[]>(`/pools/${poolId}/entries/me/all`, {}, token);
    } catch (error) {
      console.error('Error fetching my entries:', error);
      return [];
    }
  }

  async deleteEntry(poolId: string, entryId: string, token: string): Promise<void> {
    return this.request<void>(`/pools/${poolId}/entries/${entryId}`, {
      method: 'DELETE',
    }, token);
  }

  async requestBuyBack(
    poolId: string,
    entryId: string,
    token: string,
  ): Promise<any> {
    // Payment v2: no payment info at buy-back time — the $10 fee is added
    // to the entry's balance and settled from the Settle Up page.
    return this.request(`/pools/${poolId}/entries/${entryId}/buyback`, {
      method: 'POST',
    }, token);
  }

  async updateEntry(
    poolId: string,
    entryId: string,
    data: CreateEntryRequest,
    token: string
  ): Promise<Entry> {
    return this.request<Entry>(`/pools/${poolId}/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }, token);
  }

  async getEntryLineup(poolId: string, entryId: string): Promise<any> {
    return this.request<any>(`/pools/${poolId}/entries/${entryId}/lineup`);
  }

  // ============================================
  // NFL No-Repeat weekly lineup endpoints
  // ============================================

  /** Submit/update one week's 7-player lineup (merges into the entry; entryId for multi-entry). */
  async submitWeeklyLineup(
    poolId: string,
    data: { week: number; positions: Record<string, any>; entryId?: string },
    token: string,
  ): Promise<Entry> {
    return this.request<Entry>(`/pools/${poolId}/weekly-lineup`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  /** Get my weekly lineup(s) with per-slot lock state. Pass week for one week; entryId for multi-entry. */
  async getMyWeeklyLineup(poolId: string, token: string, week?: number, entryId?: string): Promise<any> {
    const params = new URLSearchParams();
    if (week !== undefined) params.set('week', String(week));
    if (entryId) params.set('entryId', entryId);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>(`/pools/${poolId}/weekly-lineup/me${q}`, {}, token);
  }

  /** Pool players annotated (used/locked/available) for a given week. entryId 'new' = fresh entry. */
  async getAvailableWeeklyPlayers(poolId: string, week: number, token: string, entryId?: string): Promise<any[]> {
    const q = `?week=${week}${entryId ? `&entryId=${encodeURIComponent(entryId)}` : ''}`;
    return this.request<any[]>(`/pools/${poolId}/weekly-lineup/available-players${q}`, {}, token);
  }

  /** Public: all entrants' lineups for a week (hidden until that week's first kickoff). */
  async getWeekLineups(poolId: string, week: number): Promise<any> {
    return this.request<any>(`/pools/${poolId}/weekly-lineup/week/${week}`);
  }

  // Admin endpoints
  async importTeams(poolId: string, data: ImportTeamsRequest, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/teams`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async importPlayers(poolId: string, data: ImportPlayersRequest, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/players`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async importSchedule(poolId: string, data: ImportScheduleRequest, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  /** Import the NFL No-Repeat regular-season schedule (Weeks 1-18, UTC kickoffs). */
  async importNflSchedule(poolId: string, data: { games: any[] }, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/nfl/schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  // F1-specific import endpoints
  async importF1Drivers(poolId: string, data: { drivers: any[] }, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/f1/drivers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async importF1Schedule(poolId: string, data: { races: any[] }, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/f1/schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  // Playoff team management endpoints
  async seedLeagueTeams(sport: string, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/teams/seed/${sport}`, {
      method: 'POST',
    }, token);
  }

  async getLeagueTeams(sport: string, token: string): Promise<{ teams: Team[] }> {
    return this.request<{ teams: Team[] }>(`/admin/teams/${sport}`, {}, token);
  }

  async selectPlayoffTeams(poolId: string, teams: Array<{ teamId: string; seed: number; metadata?: any }>, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/select-playoff-teams`, {
      method: 'POST',
      body: JSON.stringify({ teams }),
    }, token);
  }

  async importPlayersFromTeams(poolId: string, token: string): Promise<ImportResult> {
    return this.request<ImportResult>(`/admin/pools/${poolId}/import-players-from-teams`, {
      method: 'POST',
    }, token);
  }

  async getAdminPoolEntries(poolId: string, token: string): Promise<Entry[]> {
    return this.request<Entry[]>(`/admin/pools/${poolId}/entries`, {}, token);
  }

  async getPoolEvents(poolId: string, token: string): Promise<Event[]> {
    const response = await this.request<{ events: Event[] }>(`/admin/pools/${poolId}/events`, {}, token);
    return response.events;
  }

  async getEvent(eventId: string, token: string): Promise<Event> {
    const response = await this.request<{ event: Event }>(`/admin/events/${eventId}`, {}, token);
    return response.event;
  }

  async enterResult(eventId: string, data: EnterResultRequest, token: string): Promise<any> {
    return this.request(`/admin/events/${eventId}/result`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async calculateScores(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/calculate-scores`, {
      method: 'POST',
    }, token);
  }

  async adminUpdateEntry(
    poolId: string,
    entryId: string,
    data: { picks: any; entryName?: string; tiebreaker?: string },
    token: string
  ): Promise<Entry> {
    const response = await this.request<{ entry: Entry }>(`/admin/pools/${poolId}/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
    return response.entry;
  }

  async updatePoolDeadline(poolId: string, deadline: string, token: string): Promise<Pool> {
    const response = await this.request<{ pool: Pool }>(`/admin/pools/${poolId}/deadline`, {
      method: 'PUT',
      body: JSON.stringify({ deadline }),
    }, token);
    return response.pool;
  }

  async updatePool(poolId: string, data: { deadline?: string; entryFee?: number; status?: string; maxEntries?: number | null }, token: string): Promise<Pool> {
    const response = await this.request<{ pool: Pool }>(`/admin/pools/${poolId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
    return response.pool;
  }

  async completePool(poolId: string, token: string): Promise<Pool> {
    const response = await this.request<{ pool: Pool }>(`/admin/pools/${poolId}/complete`, {
      method: 'POST',
    }, token);
    return response.pool;
  }

  async updateEventTeams(
    eventId: string,
    data: { homeTeamId?: string; awayTeamId?: string },
    token: string
  ): Promise<Event> {
    const response = await this.request<{ event: Event }>(`/admin/events/${eventId}/teams`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
    return response.event;
  }

  /**
   * Flip an event's championship-game flag (2× scoring rule). Recalculates all
   * roster-based entries in the pool unless recalculate=false.
   */
  async setEventChampionship(
    eventId: string,
    isFinal: boolean,
    token: string,
    recalculate = true,
  ): Promise<{ success: boolean; message: string; eventId: string; isFinal: boolean; entriesRecalculated: number }> {
    return this.request(`/admin/events/${eventId}/championship`, {
      method: 'PUT',
      body: JSON.stringify({ isFinal, recalculate }),
    }, token);
  }

  // User endpoints
  async getCurrentUser(token?: string): Promise<any> {
    return this.request('/users/me', {}, token);
  }

  async updateUsername(username: string, token?: string): Promise<any> {
    return this.request('/users/me/username', {
      method: 'PUT',
      body: JSON.stringify({ username }),
    }, token);
  }

  // Notification preference endpoints
  async getNotificationPreferences(token: string): Promise<NotificationPrefs> {
    return this.request<NotificationPrefs>('/notifications/preferences', {}, token);
  }

  async updateNotificationPreferences(
    preferences: Partial<NotificationPrefs>,
    token: string
  ): Promise<NotificationPrefs> {
    return this.request<NotificationPrefs>('/notifications/preferences', {
      method: 'PATCH',
      body: JSON.stringify(preferences),
    }, token);
  }

  async getPoolTypes(token: string): Promise<PoolTypesResponse> {
    return this.request<PoolTypesResponse>('/notifications/pool-types', {}, token);
  }

  // Admin notification endpoints
  async sendTestEmail(email: string, token: string): Promise<{ success: boolean; message: string }> {
    return this.request('/notifications/admin/test', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, token);
  }

  async sendTestTemplateEmail(
    email: string,
    template: 'GENERIC' | 'DEADLINE_REMINDER' | 'POOL_LOCKED' | 'SCORE_UPDATE',
    poolId: string | undefined,
    token: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request('/notifications/admin/test-template', {
      method: 'POST',
      body: JSON.stringify({ email, template, poolId }),
    }, token);
  }

  async triggerDeadlineCheck(token: string): Promise<{ sent: number; errors: number }> {
    return this.request('/notifications/admin/trigger-deadline-check', {
      method: 'POST',
    }, token);
  }

  // ============================================
  // ADMIN BROADCAST
  // ============================================

  async getBroadcastAudience(
    respectPreferences: boolean,
    token: string,
  ): Promise<{ total: number; eligible: number; respectPreferences: boolean }> {
    return this.request(
      `/notifications/admin/broadcast/audience?respectPreferences=${respectPreferences ? 'true' : 'false'}`,
      {},
      token,
    );
  }

  async sendBroadcast(
    body: {
      subject: string;
      content: string;
      testMode: boolean;
      testEmail?: string;
      respectPreferences: boolean;
    },
    token: string,
  ): Promise<{ success: boolean; message: string; sent: number; failed: number; skipped: number; errors: string[] }> {
    return this.request('/notifications/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify(body),
    }, token);
  }

  async sendPoolLockedNotifications(poolId: string, token: string): Promise<{ sent: number; errors: number; skipped: number }> {
    return this.request(`/notifications/admin/pool/${poolId}/send-locked`, {
      method: 'POST',
    }, token);
  }

  async triggerScoreNotifications(token: string): Promise<{ poolsProcessed: number; totalSent: number; totalErrors: number }> {
    return this.request('/notifications/admin/trigger-score-notifications', {
      method: 'POST',
    }, token);
  }

  async sendPoolScoreUpdate(poolId: string, token: string): Promise<{ sent: number; errors: number; skipped: number }> {
    return this.request(`/notifications/admin/pool/${poolId}/send-score-update`, {
      method: 'POST',
    }, token);
  }

  async updatePoolNotificationSettings(
    poolId: string,
    settings: { scoreNotificationFrequency: 'DAILY' | 'WEEKLY' | 'AFTER_RESULTS' | 'MANUAL' },
    token: string
  ): Promise<{ success: boolean }> {
    return this.request(`/admin/pools/${poolId}/notification-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }, token);
  }

  /**
   * Export all picks for a pool
   */
  async exportPicks(
    poolId: string,
    format: 'csv' | 'xlsx' | 'pdf' = 'xlsx',
    token: string
  ): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/pools/${poolId}/export-picks?format=${format}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to export picks');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);  
      const filename = filenameMatch ? filenameMatch[1] : `picks-${Date.now()}.${format}`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export picks error:', error);
      throw error;
    }
  }

  // Payment endpoints
  async getPaymentInfo(poolId: string): Promise<PaymentInfo> {
    return this.request<PaymentInfo>(`/pools/${poolId}/payment-info`);
  }

  // Admin payment endpoints
  async getPaymentEntries(token: string, poolId?: string, status?: string): Promise<{ entries: Entry[]; count: number }> {
    const params = new URLSearchParams();
    if (poolId) params.set('poolId', poolId);
    if (status) params.set('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/payments${query}`, {}, token);
  }

  async getPoolPaymentSummary(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/payment-summary`, {}, token);
  }

  // Payment Tracking v2
  async getMyBalance(token: string): Promise<UserBalance> {
    return this.request<UserBalance>(`/users/me/balance`, {}, token);
  }

  async getOwedPayments(token: string): Promise<OwedSummary> {
    return this.request<OwedSummary>(`/admin/payments/owed`, {}, token);
  }

  async markPaid(
    data: { userId: string; entryIds?: string[]; method: string; note?: string },
    token: string,
  ): Promise<{ success: boolean; message: string; updatedCount: number; entryIds: string[] }> {
    return this.request(`/admin/payments/mark-paid`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async unmarkPaid(
    data: { entryId: string; note?: string },
    token: string,
  ): Promise<any> {
    return this.request(`/admin/payments/unmark`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async getPaymentSettings(token: string): Promise<{ settings: PaymentSettings }> {
    return this.request(`/admin/settings/payment`, {}, token);
  }

  async updatePaymentSettings(settings: PaymentSettings, token: string): Promise<any> {
    return this.request(`/admin/settings/payment`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }, token);
  }

  // Feedback endpoint
  async submitFeedback(
    data: {
      type: 'bug' | 'feature' | 'question' | 'other';
      message: string;
      email?: string;
      userAgent?: string;
      url?: string;
    },
    token?: string
  ): Promise<{ success: boolean; message: string }> {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  // ============================================
  // ADMIN FEEDBACK MANAGEMENT
  // ============================================

  async getFeedbackList(token: string, filters?: { status?: string; type?: string }): Promise<{ feedback: any[] }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.type) params.set('type', filters.type);
    const qs = params.toString();
    return this.request(`/admin/feedback${qs ? `?${qs}` : ''}`, {}, token);
  }

  async updateFeedbackStatus(feedbackId: string, status: string, token: string): Promise<any> {
    return this.request(`/admin/feedback/${feedbackId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }, token);
  }

  // ============================================
  // ADMIN KNOCKOUT MANAGEMENT
  async getKnockoutStatus(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/knockout/status`, {}, token);
  }

  async processKnockoutResults(poolId: string, data: { date: string; results: any[] }, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/knockout/results`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async processKnockoutBuyBack(poolId: string, entryId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/knockout/buyback/${entryId}`, {
      method: 'POST',
    }, token);
  }

  // ADMIN PICK6 MANAGEMENT
  async processPick6Results(poolId: string, data: { round: string; advancingTeams: string[] }, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/pick6/results`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  // ADMIN TEAM MANAGEMENT
  // ============================================

  async getAdminPoolTeams(poolId: string, token: string): Promise<{ teams: any[] }> {
    return this.request(`/admin/pools/${poolId}/teams`, {}, token);
  }

  async updateTeamName(teamId: string, data: { name?: string; shortName?: string }, token: string): Promise<any> {
    return this.request(`/admin/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  }

  // ============================================
  // BRACKET MANAGEMENT (NCAA Basketball)
  // ============================================

  async getMasterBracket(token: string): Promise<any> {
    return this.request(`/admin/bracket/master`, {}, token);
  }

  // ============================================
  // WORLD CUP — shared tournament cascade
  // ============================================

  async getWorldCupMaster(token: string): Promise<any> {
    return this.request(`/admin/world-cup/master`, {}, token);
  }

  async enterWorldCupResult(
    poolId: string,
    /**
     * winnerTeamId can be null to record a group-stage draw. Knockout-round
     * matches always need a winner (backend rejects null for those).
     * override=true required to re-submit a result for an already-completed
     * match; backend reverses the prior cascade before applying the new one.
     */
    data: {
      eventId: string;
      winnerTeamId: string | null;
      homeScore?: number;
      awayScore?: number;
      override?: boolean;
    },
    token: string,
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/world-cup/result`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async advanceWorldCupGroupStage(
    poolId: string,
    /**
     * mode='incremental' (default): additively mark teams as advanced. Teams not in
     *   the list keep their existing state. Use repeatedly as groups finish.
     * mode='finalize': mark non-listed teams as eliminated (locks the 32-team field).
     *   Must include exactly 32 advancingTeamIds.
     */
    data: { advancingTeamIds: string[]; mode?: 'incremental' | 'finalize' },
    token: string,
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/world-cup/advance-group-stage`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async getPlayInGames(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/play-in`, {}, token);
  }

  async resolvePlayInGame(
    poolId: string,
    data: { winnerId: string; loserId: string },
    token: string
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/play-in/resolve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async generateBracketMatchups(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/generate`, {
      method: 'POST',
    }, token);
  }

  async getBracketMatchups(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/matchups`, {}, token);
  }

  async getBracketMatchupsPublic(poolId: string): Promise<any> {
    return this.request(`/pools/${poolId}/bracket/matchups`);
  }

  async submitBracketResult(
    poolId: string,
    data: { eventId: string; winnerId: string; homeScore?: number; awayScore?: number; additionalPoolIds?: string[] },
    token: string
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/result`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async correctBracketResult(
    poolId: string,
    data: { eventId: string; newWinnerId?: string },
    token: string
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/correct`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  }

  async syncBracketAdvancement(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/bracket/sync`, {
      method: 'POST',
    }, token);
  }

  // ============================================
  // STATS INTEGRATION (NHL/NBA)
  // ============================================

  async fetchStatsPreview(poolId: string, date: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/fetch`, {
      method: 'POST',
      body: JSON.stringify({ date }),
    }, token);
  }

  async applyStats(poolId: string, date: string, gameExternalIds?: string[], token?: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/apply`, {
      method: 'POST',
      body: JSON.stringify({ date, gameExternalIds }),
    }, token!);
  }

  async getStatsDates(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/dates`, {}, token);
  }

  async importRosterFromApi(poolId: string, teamExternalIds: string[], token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/import-roster`, {
      method: 'POST',
      body: JSON.stringify({ teamExternalIds }),
    }, token);
  }

  /** Auto-import the NFL 18-week schedule from ESPN (NFL No-Repeat pools). */
  async autoImportNflSchedule(poolId: string, token: string, season?: number): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/auto-import-schedule`, {
      method: 'POST',
      body: JSON.stringify(season ? { season } : {}),
    }, token);
  }

  async autoImportRosters(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/auto-import-rosters`, {
      method: 'POST',
    }, token);
  }

  async assignPlayerPositions(
    poolId: string,
    assignments: Array<{ externalId: string; firstName: string; lastName: string; teamId: string; jerseyNumber: string; position: string }>,
    token: string,
  ): Promise<any> {
    return this.request(`/admin/pools/${poolId}/stats/assign-positions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignments }),
    }, token);
  }

  async getPoolPlayersAdmin(poolId: string, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/players`, {}, token);
  }

  async togglePlayerExclusion(poolId: string, playerIds: string[], excluded: boolean, token: string): Promise<any> {
    return this.request(`/admin/pools/${poolId}/players/exclude`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerIds, excluded }),
    }, token);
  }

  async updatePlayerPosition(playerId: string, position: string, token: string): Promise<any> {
    return this.request(`/admin/players/${playerId}/position`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position }),
    }, token);
  }
}

export const api = new ApiService();
